import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Tag, 
  Space, 
  Typography, 
  Divider,
  message,
  Modal,
  Form,
  DatePicker,
  Alert,
  Progress
} from 'antd';
import { 
  CheckCircleOutlined, 
  DollarOutlined,
  ShoppingOutlined,
  RocketOutlined,
  TeamOutlined,
  HomeOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import { ApiConfig } from '../../services/ApiConfigs';
import planService from '../../services/planService';
import useFetch from '../../Hooks/FetchData';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { routes } from '../../utils/routes.config';
import { setUserData } from '../../redux/actions/DataActions';
import store from '../../redux/store';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

const SelectPlan = () => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetail = useSelector((state: any) => state.UserData.userData);
  const user = userDetail?.user || userDetail;
  const storeId = user?.storeId;

  const { data: plans, loading: plansLoading, refetch: refetchPlans } = useFetch(
    ApiConfig.PLANS_ACTIVE,
    { trigger: true }
  );

  const { data: currentStorePlan, loading: planLoading, refetch: refetchStorePlan } = useFetch(
    storeId ? `${ApiConfig.PLANS_STORE}/${storeId}` : null,
    { trigger: !!storeId }
  );

  const handleSelectPlan = (plan: any) => {
    setSelectedPlan(plan);
    setModalVisible(true);
    // Reset form and set planId
    form.resetFields();
    form.setFieldsValue({
      planId: plan.id,
    });
    console.log('Plan selected:', plan);
  };

  const handleSubmit = async (values: any) => {
    console.log('Form values:', values);
    console.log('Store ID:', storeId);
    
    if (!storeId) {
      message.error('Store ID not found. Please contact support.');
      return;
    }

    // Validate planId is present
    if (!values.planId) {
      message.error('Please select a plan');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare request body
      const requestBody = {
        storeId: storeId,
        planId: values.planId,
        // Only send endDate if it's a subscription plan and user wants to customize
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
      };
      
      console.log('Request body:', requestBody);
      
      // Backend will use current time for startDate automatically
      const response = await planService.assignPlanToStore(requestBody);
      
      console.log('API Response:', response);
      
      if (response.success) {
        message.success('Plan selected successfully!');
        setModalVisible(false);
        form.resetFields();
        setSelectedPlan(null);
        const currentUserData = store.getState().UserData?.userData;
        if (currentUserData) {
          dispatch(setUserData({ ...currentUserData, hasActivePlan: true }));
        }
        refetchStorePlan();
        refetchPlans();
        navigate(routes.DASHBOARD);
      } else {
        message.error(response.message || 'Failed to select plan');
      }
    } catch (error: any) {
      console.error('Plan selection error:', error);
      message.error(error.message || error.response?.data?.message || 'Failed to select plan');
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = currentStorePlan?.plan;
  const storePlanData = currentStorePlan;
  const plansList = Array.isArray(plans) ? plans : [];

  const getUsagePercentage = (current: number, max: number) => {
    if (!max || max === 0) return 0;
    return Math.min((current / max) * 100, 100);
  };

  return (
    <MainLayout>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <Space>
            <ShoppingCartOutlined />
            <span>Select Plan</span>
          </Space>
        </Title>
        <Paragraph style={{ marginTop: 8, color: '#666' }}>
          Choose a plan that best fits your delivery management needs
        </Paragraph>
      </div>

      {/* Current Plan Card */}
      {currentPlan && (
        <Card
          style={{ marginBottom: 24, border: '2px solid #52c41a' }}
          title={
            <Space>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              <Text strong>Current Plan</Text>
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small">
                <Text strong style={{ fontSize: 20 }}>
                  {currentPlan.name}
                </Text>
                <Text type="secondary">
                  {currentPlan.currencySymbol} {currentPlan.price} / {currentPlan.billingPeriod}
                </Text>
                {currentPlan.description && (
                  <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                    {currentPlan.description}
                  </Paragraph>
                )}
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                  <Space>
                    <HomeOutlined />
                    <Text>Warehouses</Text>
                  </Space>
                  <Progress
                    percent={getUsagePercentage(storePlanData?.currentWarehouses || 0, currentPlan.maxWarehouses)}
                    format={() => `${storePlanData?.currentWarehouses || 0} / ${currentPlan.maxWarehouses}`}
                    status={getUsagePercentage(storePlanData?.currentWarehouses || 0, currentPlan.maxWarehouses) >= 90 ? 'exception' : 'active'}
                  />
                </div>
                <div>
                  <Space>
                    <TeamOutlined />
                    <Text>Drivers</Text>
                  </Space>
                  <Progress
                    percent={getUsagePercentage(storePlanData?.currentDrivers || 0, currentPlan.maxDrivers)}
                    format={() => `${storePlanData?.currentDrivers || 0} / ${currentPlan.maxDrivers}`}
                    status={getUsagePercentage(storePlanData?.currentDrivers || 0, currentPlan.maxDrivers) >= 90 ? 'exception' : 'active'}
                  />
                </div>
                {currentPlan.maxOrders && (
                  <div>
                    <Space>
                      <ShoppingCartOutlined />
                      <Text>Orders (Monthly)</Text>
                    </Space>
                    <Progress
                      percent={getUsagePercentage(storePlanData?.currentOrders || 0, currentPlan.maxOrders)}
                      format={() => `${storePlanData?.currentOrders || 0} / ${currentPlan.maxOrders}`}
                      status={getUsagePercentage(storePlanData?.currentOrders || 0, currentPlan.maxOrders) >= 90 ? 'exception' : 'active'}
                    />
                  </div>
                )}
              </Space>
            </Col>
          </Row>
          {storePlanData?.endDate && (
            <Alert
              message={`Plan expires on ${moment(storePlanData.endDate).format('MMMM DD, YYYY')}`}
              type="warning"
              style={{ marginTop: 16 }}
              showIcon
            />
          )}
        </Card>
      )}

      {/* Available Plans */}
      <Card
        title={
          <Space>
            <DollarOutlined />
            <Text strong>Available Plans</Text>
          </Space>
        }
        loading={plansLoading}
      >
        <Row gutter={[24, 24]}>
          {plansList?.map((plan: any) => {
            const isCurrentPlan = currentPlan?.id === plan.id;
            return (
              <Col xs={24} sm={12} lg={8} key={plan.id}>
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    border: isCurrentPlan ? '2px solid #52c41a' : '1px solid #d9d9d9',
                    position: 'relative',
                  }}
                  actions={[
                    <Button
                      type={isCurrentPlan ? 'default' : 'primary'}
                      block
                      disabled={isCurrentPlan}
                      onClick={() => handleSelectPlan(plan)}
                      icon={isCurrentPlan ? <CheckCircleOutlined /> : <RocketOutlined />}
                    >
                      {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                    </Button>
                  ]}
                >
                  {plan.isPopular && (
                    <Tag
                      color="gold"
                      style={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 1,
                      }}
                    >
                      Popular
                    </Tag>
                  )}
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                      <Space>
                        <Title level={3} style={{ margin: 0 }}>
                          {plan.name}
                        </Title>
                        {plan.planType === 'one_time' ? (
                          <Tag color="purple">One-Time</Tag>
                        ) : (
                          <Tag color="blue">Subscription</Tag>
                        )}
                      </Space>
                      <div style={{ marginTop: 8 }}>
                        <Text strong style={{ fontSize: 24, color: '#1890ff' }}>
                          {plan.currencySymbol} {plan.price}
                        </Text>
                        {plan.planType === 'subscription' && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            / {plan.billingPeriod}
                          </Text>
                        )}
                      </div>
                    </div>

                    {plan.description && (
                      <Paragraph style={{ margin: 0, color: '#666' }}>
                        {plan.description}
                      </Paragraph>
                    )}

                    <Divider style={{ margin: '16px 0' }} />

                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Space>
                        <HomeOutlined />
                        <Text>{plan.maxWarehouses} Warehouse{plan.maxWarehouses !== 1 ? 's' : ''}</Text>
                      </Space>
                      <Space>
                        <TeamOutlined />
                        <Text>{plan.maxDrivers} Driver{plan.maxDrivers !== 1 ? 's' : ''}</Text>
                      </Space>
                      {plan.maxOrders ? (
                        <Space>
                          <ShoppingCartOutlined />
                          <Text>{plan.maxOrders} Orders/month</Text>
                        </Space>
                      ) : (
                        <Space>
                          <ShoppingCartOutlined />
                          <Text>Unlimited Orders</Text>
                        </Space>
                      )}
                    </Space>
                  </Space>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* Plan Selection Modal */}
      <Modal
        title="Confirm Plan Selection"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedPlan(null);
        }}
        footer={null}
        width={500}
      >
        {selectedPlan && (
          <div>
            <Alert
              message={`You are about to select: ${selectedPlan.name}`}
              description={
                selectedPlan.planType === 'one_time'
                  ? `${selectedPlan.currencySymbol} ${selectedPlan.price} (One-Time Payment)`
                  : `${selectedPlan.currencySymbol} ${selectedPlan.price} / ${selectedPlan.billingPeriod}`
              }
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                planId: selectedPlan?.id,
                endDate: null,
              }}
            >
              {/* Hidden field to ensure planId is always in form values */}
              <Form.Item name="planId" hidden>
                <input type="hidden" />
              </Form.Item>
              
              <Alert
                message="Plan will start immediately"
                description="The plan will be activated from today. Start date is automatically set to current time."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              {selectedPlan?.planType === 'subscription' && (
                <Form.Item
                  name="endDate"
                  label="End Date (Optional - Leave empty to auto-calculate based on billing period)"
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              )}
              
              {selectedPlan?.planType === 'one_time' && (
                <Alert
                  message="One-Time Plan"
                  description="This is a one-time payment plan with lifetime access. No end date required."
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                >
                  Confirm Selection
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
};

export default SelectPlan;

